<template>
	<div class="b-selector" :class="classes">
		<label
			class="b-selector__option"
			v-for="option in options"
			:key="option[valueKey]"
		>
			<input :type="inputType" v-model="innerModel" :value="option[valueKey]" />
			{{ $t ? $t(option.label) : option.label }}
		</label>
	</div>
</template>

<script>
export default {
	name: "b-selector",
	props: {
		value: {
			type: [String, Number, Array],
			default: null,
		},
		options: {
			type: Array,
			default: () => [], // { id: string|number, label: string}[]
		},
		size: {
			type: String,
			default: "m",
			validator: (value) => ["s", "m"].includes(value),
		},
		design: {
			type: String,
			default: "default",
			valdator: (val) => ["split", "default"].includes(val),
		},
		multiple: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return {};
	},
	computed: {
		classes() {
			return [
				`b-selector--${this.size}`,
				{
					"b-selector--designSplit": this.design === "split",
					"b-selector--designDefault": this.design === "default",
				},
			];
		},
		inputType() {
			return this.multiple ? "checkbox" : "radio";
		},
		valueKey: (() => {
			const primaryValueKey = "id";
			const secondaryValueKeys = ["value", "key"];
			const allKeys = [primaryValueKey, ...secondaryValueKeys];

			return function() {
				const sampleOption = this.options[0];

				for (const key in allKeys) {
					if (Object.prototype.hasOwnProperty.call(sampleOption, key)) {
						return key;
					}
				}

				return primaryValueKey;
			};
		})(),

		innerModel: {
			get() {
				return this.value;
			},
			set(value) {
				this.$emit("input", value);
			},
		},
	},
};
</script>

<style lang="scss">
.b-selector {
	row-gap: 1rem;
	display: inline-flex;
	flex-wrap: wrap;

	&--designSplit {
		gap: 12px;
	}

	input {
		display: none;
	}
}

.b-selector__option {
	// Unchecked
	--color-unchecked: var(--color-primary-600);
	--color-unckecked-hover: var(--color-primary-700);
	--color-unckecked-active: var(--color-primary-800);
	--color-unckecked-disabled: var(--color-neutral-175);

	--border-color-unchecked: var(--color-primary-600);
	--border-color-unchecked-hover: var(--color-primary-700);
	--border-color-unchecked-active: var(--color-primary-800);
	--border-color-unchecked-disabled: var(--color-neutral-175);

	// Checked
	--color-checked: #fff;

	--background-color-checked: var(--color-primary-600);
	--background-color-checked-hover: var(--color-primary-700);
	--background-color-checked-active: var(--color-primary-800);
	--background-color-checked-disabled: var(--color-neutral-175);

	display: inline-flex;
	align-items: center;
	justify-content: center;
	border: 1px solid transparent;
	white-space: nowrap;
	cursor: pointer;
	height: var(--size);
	min-width: var(--size);

	.b-selector--designSplit & {
		border-radius: 100px;
	}

	.b-selector--designDefault & {
		&:first-child {
			border-top-left-radius: 100px;
			border-bottom-left-radius: 100px;
		}

		&:last-child {
			border-top-right-radius: 100px;
			border-bottom-right-radius: 100px;
		}

		&:not(:last-child) {
			margin-right: -1px;
		}
	}

	.b-selector--s & {
		--size: 32px;
		padding-inline: 16px;
		font-size: var(--font-size-xs);
	}

	.b-selector--m & {
		--size: 44px;
		padding-inline: 16px;
		font-size: var(--font-size-m);
	}

	&:not(:disabled) {
		position: relative;
		z-index: 1;
	}

	&:has(:not(input:checked)) {
		color: var(--color-unchecked);
		border-color: var(--border-color-unchecked);

		&:not(:disabled) {
			&:is(:hover, :focus) {
				color: var(--color-unckecked-hover);
				border-color: var(--border-color-unchecked-hover);
				box-shadow: inset 0 0 0 1px var(--border-color-unchecked-hover);
			}

			&:active {
				color: var(--color-unckecked-active);
				border-color: var(--border-color-unchecked-active);
				box-shadow: inset 0 0 0 1px var(--border-color-unchecked-active);
			}
		}

		&:disabled {
			color: var(--color-unckecked-disabled);
			border-color: var(--border-color-unchecked-disabled);
			cursor: not-allowed;
		}
	}

	&:has(input:checked) {
		color: var(--color-checked);
		background-color: var(--background-color-checked);

		&:not(:disabled) {
			&:is(:hover, :focus) {
				background-color: var(--background-color-checked-hover);
			}

			&:active {
				background-color: var(--background-color-checked-active);
			}
		}

		&:disabled {
			background-color: var(--background-color-checked-disabled);
			cursor: not-allowed;
		}
	}
}

.colorCtx-dark {
	.b-selector__option {
		// Unchecked
		--color-unchecked: #fff;
		--color-unckecked-hover: #fff;
		--color-unckecked-active: #fff;
		--color-unckecked-disabled: #fff;

		--border-color-unchecked: var(--color-primary-400);
		--border-color-unchecked-hover: var(--color-primary-400);
		--border-color-unchecked-active: var(--color-primary-400);
		--border-color-unchecked-disabled: var(--color-neutral-400);

		// Checked
		--color-checked: var(--color-primary-600);

		--background-color-checked: #fff;
		--background-color-checked-hover: #fff;
		--background-color-checked-active: #fff;
		--background-color-checked-disabled: #fff;
	}
}
</style>
